//react
import React from "react";
import {connect} from "react-redux";

//packages
import {PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip} from "recharts";
import Moment from "moment";
import {startCase} from "lodash";

//utils
import {updateCampaignAggregates} from "../../store/api/calls";
import {pullCampaignDoorCanvassPasses} from "../../store/api/calls/doors";
import {setDashboardParams} from "../../store/actions";
import api from "../../store/api/api";

//components
import NavBaseTemplate from "../templates/NavBaseTemplate";
import FromToDatePickerInput from "../theme/forms/FromToDatePickerInput";
import FeedbackTile from "../theme/modals/FeedbackTile";
import DashboardOfficeSelector from "../theme/dashboard/selectors/DashboardOfficeSelector";
import DoorCanvassPassSelector from "../theme/dashboard/selectors/DoorCanvassPassSelector";
import DoorCanvassPassSelectionModal from "../theme/dashboard/selectors/DoorCanvassPassSelectionModal";
import DashTabs from "../theme/menus/DashTabs";

class DoorCanvassStatusReport extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            loading: false,
            error: null,
            data: [],
            displayData: [],
            displayDCPSelectorModal: false
        };
        this.SUBMIT_DATE_FORMAT = "YYYY-MM-DD";
        this.ERROR_TIMEOUT = null;
        this.ERROR_TIMEOUT_DURATION = 3000;
        this._isMounted = false;
        this._abortController = null;
    }

    handleDateChange = (start, end) => {
        this.props.setDashboardParams({
            start: start.getTime(),
            end: end.getTime(),
        });
    };

    componentDidMount() {
        this._isMounted = true;
        this._abortController = new AbortController();
        this.setState({loading: true});
        this.props.setDashboardParams({selectedCanvasser: 0});

        let promises = [
            pullCampaignDoorCanvassPasses(),
            this._updateCampaignAggregates(),
        ];

        Promise.allSettled(promises).then(() => {
            if (!this._isMounted) return;
            console.log(this.props.doorCanvassPasses);
            if (!this.props.doorCanvassPasses.length) {
                this.setState({loading: false, displayDCPSelectorModal: false});
            } else {
                if (
                    this.props.dashboardParams.selectedDoorCanvassPass &&
                    this.props.doorCanvassPasses
                        .map((d) => d.id)
                        .includes(this.props.dashboardParams.selectedDoorCanvassPass)
                ) {
                    this._getData();
                } else {
                    this.setState({loading: false, displayDCPSelectorModal: true});
                }
            }
        });
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.focusedCampaign.id !== this.props.focusedCampaign.id) {
            this.setState({loading: true});
            pullCampaignDoorCanvassPasses().finally(() => {
                if (!this._isMounted) return;
                this.props.setDashboardParams({
                    selectedOffice: 0,
                    selectedDoorCanvassPass: 0,
                });
                this.setState({loading: false});
                //display modal only if pass not set
                this.setState({
                    displayDCPSelectorModal: !!this.props.doorCanvassPasses.length,
                });
            });
        }
        if (
            prevProps.dashboardParams.start !== this.props.dashboardParams.start ||
            prevProps.dashboardParams.end !== this.props.dashboardParams.end ||
            prevProps.dashboardParams.selectedDoorCanvassPass !==
            this.props.dashboardParams.selectedDoorCanvassPass
        ) {
            this._getData();
            this._updateCampaignAggregates();
        }

        if (
            prevProps.dashboardParams.selectedOffice !==
            this.props.dashboardParams.selectedOffice
        ) {
            this.setState({
                displayData: this.parseDataForDisplay(),
            });
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        clearTimeout(this.ERROR_TIMEOUT);
        if (this._abortController) {
            this._abortController.abort();
        }
    }

    _updateCampaignAggregates = () => {
        return updateCampaignAggregates(
            Moment(this.props.dashboardParams.start).format(this.SUBMIT_DATE_FORMAT),
            Moment(this.props.dashboardParams.end).format(this.SUBMIT_DATE_FORMAT)
        );
    };

    _getFocusedDoorCanvassPass = () =>
        this.props.doorCanvassPasses.find(
            (dcp) => dcp.id === this.props.dashboardParams.selectedDoorCanvassPass
        ) || {};

    _getData = (dcp_id) => {
        if (!dcp_id) dcp_id = this.props.dashboardParams.selectedDoorCanvassPass;

        if (!dcp_id) {
            this.setState({loading: false, displayData: []});
            return new Promise((resolve) => resolve());
        }

        this.setState({loading: true});

        api
            .get("door-status-counts/", {
                start: new Moment(this.props.dashboardParams.start).format(
                    this.SUBMIT_DATE_FORMAT
                ),
                end: new Moment(this.props.dashboardParams.end).format(
                    this.SUBMIT_DATE_FORMAT
                ),
                doorcanvasspass: dcp_id,
            })
            .then((res) => {
                if (!this._isMounted) return;
                this.setState({
                    data: res.data || [],
                    displayData: this.parseDataForDisplay(res.data),
                    error: null,
                    loading: false,
                });
            })
            .catch((err) => {
                if (!this._isMounted) return;
                if (!err.response || !err.response.status) {
                    this._setError("Unable to connect");
                } else {
                    this._setError("Server error.");
                }
                this.setState({loading: false});
            });
    };

    _setError = (errMsg) => {
        clearTimeout(this.ERROR_TIMEOUT);
        if (!this._isMounted) return;
        this.setState({error: errMsg}, () => {
            if (!this._isMounted) return;
            this.ERROR_TIMEOUT = setTimeout(() => {
                if (this._isMounted) {
                    this.setState({error: null});
                }
            }, this.ERROR_TIMEOUT_DURATION);
        });
    };

    parseDataForDisplay = (sourceData = this.state.data) => {
        let displayData = [];
        if (!sourceData || !sourceData.length) return displayData;

        if (this.props.dashboardParams.selectedOffice) {
            sourceData = sourceData.filter(
                (d) => d.office_id === this.props.dashboardParams.selectedOffice
            );
        }

        displayData = sourceData.map((d) => {
            const statuses = d.statuses || [];
            let total_count = statuses.reduce((r, s) => r + (s.count || 0), 0);
            return {
                ...d,
                name: `${startCase(d.firstname || "")} ${startCase(
                    d.lastname || ""
                )} (${total_count})`,
                total_count,
                chartData: statuses.map((s) => ({
                    name: s.status,
                    id: s.status,
                    value: s.count || 0,
                    label: startCase(s.status || ""),
                    color: s.status_color,
                })),
            };
        });

        //we are not going to make a total pie, to be placed in the zero category.

        let selectedDCP = this._getFocusedDoorCanvassPass();
        let totals_data = {
            name: "Totals for Pass",
            total_count: 0,
        };

        totals_data.chartData = displayData.reduce((r, d) => {
            const statuses = d.statuses || [];

            statuses.forEach((s) => {
                let count = s.count || 0;
                let matching_obj = r.find((_d) => _d.name === s.status);
                if (matching_obj) {
                    matching_obj["value"] += count;
                } else {
                    r.push({
                        name: s.status,
                        id: s.status,
                        value: count,
                        label: startCase(s.status || ""),
                        color: s.status_color,
                    });
                }

                totals_data.total_count += count;
            });

            return r;
        }, []);

        //add percentage to the chart data labels
        if (totals_data.total_count > 0) {
            totals_data.chartData.forEach((d) => {
                d.label = `${d.label} (${Math.round(
                    (d.value / totals_data.total_count) * 100
                )}%)`;
            });
        }

        if (totals_data.total_count) {
            totals_data.name = `${totals_data.name} (${totals_data.total_count})`;
        }

        return [totals_data, ...displayData];
    };

    // Helper function to darken color for borders
    _darkenColor = (color, percent) => {
        if (!color || color === 'transparent') return '#000000';
        if (color.startsWith('#')) {
            const num = parseInt(color.replace('#', ''), 16);
            const r = Math.max(0, ((num >> 16) & 0xff) * (1 - percent));
            const g = Math.max(0, (((num >> 8) & 0xff) * (1 - percent)));
            const b = Math.max(0, ((num & 0xff) * (1 - percent)));
            return '#' + (0x1000000 + (r << 16) + (g << 8) + b).toString(16).slice(1);
        }
        return color;
    };

    // Custom tooltip component
    _renderTooltip = (props) => {
        if (!props || !props.active || !props.payload || !props.payload[0]) return null;
        const data = props.payload[0];
        const payload = data.payload || {};
        const value = data.value || payload.value || 0;
        const total = payload.total || 0;
        const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
        const label = payload.label || payload.name || data.name || 'Unknown';
        
        return (
            <div style={{
                backgroundColor: 'white',
                border: '1px solid #ccc',
                borderRadius: '4px',
                padding: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
                <p style={{margin: 0, fontWeight: 'bold'}}>{label}</p>
                <p style={{margin: '4px 0 0 0', color: '#666'}}>
                    Value: {value} ({percentage}%)
                </p>
            </div>
        );
    };

    // Render main totals chart
    renderMainChart = (totalsObject) => {
        const {chartData} = totalsObject;
        if (!chartData || chartData.length === 0) return null;

        // Calculate total for percentage calculations
        const total = chartData.reduce((sum, d) => sum + (d.value || 0), 0);
        const chartDataWithTotal = chartData.map(d => ({...d, total}));

        // Custom label function for legend
        const renderCustomLabel = (entry) => {
            return entry.label || entry.name;
        };

        return (
            <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                    <Pie
                        data={chartDataWithTotal}
                        cx="50%"
                        cy="50%"
                        innerRadius="50%"
                        outerRadius="80%"
                        paddingAngle={4}
                        dataKey="value"
                        stroke="#fff"
                        strokeWidth={3}
                    >
                        {chartDataWithTotal.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                fill={entry.color || '#8884d8'}
                                stroke={this._darkenColor(entry.color, 0.2)}
                            />
                        ))}
                    </Pie>
                    <Tooltip content={this._renderTooltip} />
                    <Legend
                        formatter={renderCustomLabel}
                        wrapperStyle={{paddingTop: '20px'}}
                        iconType="circle"
                    />
                </PieChart>
            </ResponsiveContainer>
        );
    };

    // Render sub chart for individual canvassers
    renderSubChart = (chartData, name) => {
        if (!chartData || chartData.length === 0) return null;

        // Calculate total for percentage calculations
        const total = chartData.reduce((sum, d) => sum + (d.value || 0), 0);
        const chartDataWithTotal = chartData.map(d => ({...d, total}));

        return (
            <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                    <Pie
                        data={chartDataWithTotal}
                        cx="50%"
                        cy="50%"
                        innerRadius="40%"
                        outerRadius="70%"
                        paddingAngle={4}
                        dataKey="value"
                        stroke="#fff"
                        strokeWidth={1}
                    >
                        {chartDataWithTotal.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                fill={entry.color || '#8884d8'}
                                stroke={this._darkenColor(entry.color, 0.2)}
                            />
                        ))}
                    </Pie>
                    <Tooltip content={this._renderTooltip} />
                </PieChart>
            </ResponsiveContainer>
        );
    };

    renderChartGrid = () => {
        let displayData = [...this.state.displayData];

        // if (this.props.dashboardParams.selectedOffice) {
        //     displayData = displayData.filter(d => d.office_id === this.props.dashboardParams.selectedOffice)
        // }

        let totalsObject = displayData.shift();

        if (!totalsObject)
            totalsObject = {
                name: "No data during this period.",
                chartData: [
                    {
                        name: "nada",
                        id: "nada",
                        label: "Nada",
                        color: "#dfdfdf",
                        value: 1,
                    },
                ],
            };

        return (
            <div className={"chart-grid"}>
                <div className={"top-chart full-width"}>
                    <h5 className={"primary-milligram-text"}>{totalsObject.name}</h5>
                    {this.renderMainChart(totalsObject)}
                </div>
                <div className={"row"} style={{flexWrap: "wrap"}}>
                    {displayData.map((d, index) => {
                        return (
                            <div key={`${d.id || index}-canvasser-${index}`} className={"column column-25"}>
                                <div className={"row-chart"}>
                                    <p className={"primary-milligram-text"}>{d.name}</p>
                                    {this.renderSubChart(d.chartData, d.name)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    render() {
        return (
            <NavBaseTemplate>
                <DashTabs/>
                <h3 className={"primary-text"}>Door Canvass Statuses</h3>
                <div className="button-table-top-wrapper">
                    <FromToDatePickerInput
                        handleDateChange={this.handleDateChange}
                        from={new Date(this.props.dashboardParams.start)}
                        to={new Date(this.props.dashboardParams.end)}
                    />
                    <DashboardOfficeSelector/>
                    <DoorCanvassPassSelector/>
                </div>

                {!this.props.doorCanvassPasses.length && (
                    <p>There are no door canvasses in this campaign.</p>
                )}

                {!this.state.loading && this.state.displayData.length > 0 && this.renderChartGrid()}

                <DoorCanvassPassSelectionModal
                    onClose={() => this.setState({displayDCPSelectorModal: false})}
                    open={this.state.displayDCPSelectorModal}
                    afterSelect={(selectedPassId) =>
                        this.setState({displayDCPSelectorModal: false})
                    }
                />

                <FeedbackTile
                    text={
                        <div>
                            <i
                                className=" fas fa-asterisk fa-spin"
                                style={{marginRight: "15px"}}
                            />
                            Loading...
                        </div>
                    }
                    visible={this.state.loading}
                />

                {/*use feedback tile icon, text, state*/}
                <FeedbackTile
                    text={
                        <div>
                            <i
                                className="fas fa-exclamation-triangle"
                                style={{marginRight: "15px"}}
                            />
                            {this.state.error}
                        </div>
                    }
                    visible={this.state.error}
                    backgroundColor={"red"}
                />
            </NavBaseTemplate>
        );
    }
}

const mapStateToProps = (state) => ({
    campaigns: state.campaigns?.data,
    focusedCampaign: state.session.focusedCampaign,
    is_admin: state.permissions.is_admin,
    dashboardParams: state.dashboardParams,
    doorCanvassPasses: state.doorCanvassPasses.data,
});

const mapDispatchToProps = (dispatch) => ({
    setDashboardParams: (data) => dispatch(setDashboardParams(data)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(DoorCanvassStatusReport);
